import { useKFlags } from '@/hooks/use-k-flags';
import {
  Box,
  Flex,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from '@chakra-ui/react';
import { SlashCircle01 } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import { AiScoringConstants } from '../aiScoringConstants';

const { thresholdMap } = AiScoringConstants;

interface ScoreLegendProps {
  label: string;
  bg: string;
  from: number;
  to: number;
}

const ScoreLegend = ({ label, bg, from, to }: ScoreLegendProps) => (
  <Box textAlign="center">
    <KText variant="overhead">
      <KText as="span" color={bg}>
        ●
      </KText>{' '}
      {label.toUpperCase()} ({from} - {to}%)
    </KText>
  </Box>
);

const getSliderColor = (value: number): string => {
  if (value <= thresholdMap.low.to) return thresholdMap.low.bg;
  if (value <= thresholdMap.average.to) return thresholdMap.average.bg;
  return thresholdMap.high.bg;
};

interface AutoRejectSectionProps {
  enabled: boolean;
  onToggle: (_value: boolean) => void;
  threshold: number;
  onThresholdChange: (_value: number) => void;
  /** Layout overrides so the section fits both the drawer wizard and the edit view */
  containerProps?: React.ComponentProps<typeof Flex>;
}

/**
 * Reusable auto-reject control (Yes/No toggle + threshold slider + legend + note).
 * Shared by the Scoring agent setup wizard ("Verify attributes" step) and the
 * "Edit Scoring agent" info view so the copy and behaviour stay in sync.
 */
export const AutoRejectSection = ({
  enabled,
  onToggle,
  threshold,
  onThresholdChange,
  containerProps,
}: AutoRejectSectionProps) => {
  const { feJobTeammates } = useKFlags();

  return (
    <Flex
      flexDir="column"
      bg="secondary-25"
      p="16px 24px"
      borderRadius="6px"
      id="ai-v2-auto-reject-note"
      {...containerProps}
    >
      <Flex gap="12px" alignItems="center" pb="12px" flexWrap="wrap">
        <Flex gap="6px" alignItems="center">
          <SlashCircle01 size={18} className="kula-icon" />
          <KText variant="heading_4">
            {feJobTeammates
              ? 'Do you want to auto-reject based on Scoring agent score?'
              : 'Do you want to auto-reject based on AI score?'}
          </KText>
        </Flex>
        <Flex
          gap="0"
          bg="white"
          borderRadius="6px"
          border="1px solid"
          borderColor="secondary-100"
          overflow="hidden"
          height="32px"
          alignItems="center"
          flexShrink={0}
        >
          <KText
            p="5px 11px"
            cursor="pointer"
            color={enabled ? 'white' : 'secondary-700'}
            bg={enabled ? 'primary-500' : 'white'}
            _hover={{ bg: enabled ? 'primary-600' : 'primary-50' }}
            onClick={() => onToggle(true)}
          >
            Yes
          </KText>
          <KText
            p="5px 11px"
            cursor="pointer"
            color={!enabled ? 'white' : 'secondary-700'}
            bg={!enabled ? 'primary-500' : 'white'}
            _hover={{ bg: !enabled ? 'primary-600' : 'primary-50' }}
            onClick={() => onToggle(false)}
          >
            No
          </KText>
        </Flex>
      </Flex>
      {enabled && (
        <Flex flexDir="column" gap="16px" pb="8px">
          <Box w="100%">
            <Slider
              value={threshold}
              min={0}
              max={100}
              step={1}
              onChange={onThresholdChange}
              colorScheme="primary"
            >
              <SliderTrack>
                <SliderFilledTrack bg={getSliderColor(threshold)} />
              </SliderTrack>
              <SliderThumb
                w="48px"
                h="24px"
                color="white"
                bg={getSliderColor(threshold)}
                borderRadius="6px"
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
              >
                <KText color="base-white">{threshold}%</KText>
              </SliderThumb>
            </Slider>
            <Flex
              position="relative"
              w="100%"
              mt="14px"
              h="24px"
              gap="24px"
              justifyContent="space-between"
            >
              <Flex w="45%">
                <ScoreLegend {...thresholdMap.low} />
              </Flex>
              <Flex w="55%" justifyContent="space-between" gap="24px">
                <ScoreLegend {...thresholdMap.average} />
                <ScoreLegend {...thresholdMap.high} />
              </Flex>
            </Flex>
          </Box>
          <KText fontSize="13px">
            <KText as="span" variant="heading_4">
              Note:
            </KText>{' '}
            Candidates who score below the set threshold will be automatically
            rejected. Start with a threshold of 50% or less and adjusting it
            later based on effectiveness.
          </KText>
        </Flex>
      )}
    </Flex>
  );
};
